<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero con imagen de fondo -->
    <ImageBackgroundSection
      size="compact"
      :image-url="historia?.imagen || ''"
      :alt="historia?.nombre || 'Historias'"
      title="Historias"
      subtitle="Voces de quienes viven y sienten las bochas cordobesas"
    />

    <!-- Detalle -->
    <article
      v-if="historia"
      class="px-6 lg:px-0 py-12 lg:pt-24 lg:pb-0 max-w-7xl mx-auto"
    >
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="mensaje" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">Historias</span>
      </div>

      <h2
        class="text-3xl sm:text-4xl font-semibold text-white mb-4 leading-tight"
      >
        {{ historia.nombre }}
      </h2>
      <p class="text-base sm:text-3xl font-semibold text-white mb-6">
        {{ historia.club }} — {{ historia.años }} años jugando
      </p>

      <!-- Botones de compartir -->
      <div
        class="flex items-center gap-4 mb-6 sm:mb-8 py-3 border-y border-white/10"
      >
        <button
          @click="compartirWhatsapp"
          type="button"
          class="hover:opacity-80 transition-opacity"
          aria-label="Compartir en WhatsApp"
        >
          <IconFbochas name="whatsapp" :size="22" color="#a52121" />
        </button>
        <button
          @click="compartirFacebook"
          type="button"
          class="hover:opacity-80 transition-opacity"
          aria-label="Compartir en Facebook"
        >
          <IconFbochas name="facebook" :size="22" color="#a52121" />
        </button>
        <button
          @click="compartirInstagram"
          type="button"
          class="hover:opacity-80 transition-opacity"
          aria-label="Compartir en Instagram"
        >
          <IconFbochas name="instagram" :size="22" color="#a52121" />
        </button>
      </div>

      <!-- Imagen principal -->
      <div class="h-[450px] overflow-hidden rounded-xl mb-8 bg-bochas-bg">
        <img
          v-if="historia.imagen"
          :src="historia.imagen"
          :alt="historia.nombre"
          class="w-full h-full object-cover object-center"
          loading="lazy"
        />
        <div
          v-else
          class="flex h-full w-full items-center justify-center bg-degradado-bochas text-6xl font-bold text-white"
        >
          {{ initials(historia.nombre) }}
        </div>
      </div>

      <!-- Quote grande -->
      <blockquote
        class="text-2xl sm:text-3xl font-medium text-white italic text-center mb-8 leading-snug"
      >
        "{{ historia.quote }}"
      </blockquote>

      <!-- Texto del artículo -->
      <div
        class="space-y-4 text-white text-sm sm:text-base font-normal leading-relaxed"
      >
        <p v-for="(parrafo, idx) in historia.texto" :key="idx">
          {{ parrafo }}
        </p>
      </div>

      <!-- Volver -->
      <div class="pt-24">
        <VolverButton to="/historias" />
      </div>
    </article>

    <section v-else class="px-6 lg:px-0 py-20 max-w-7xl mx-auto text-center">
      <p class="text-bochas-text-muted text-base sm:text-lg">
        Historia no encontrada.
      </p>
      <div class="mt-4">
        <VolverButton to="/historias" text="Volver a historias" />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { applyContentSeo, applyNotFoundSeo } from "@/services/seo/seoService";
import { useRoute } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import VolverButton from "@/components/Common/VolverButton.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";

const route = useRoute();
const slug = computed(() => route.params.slug);

// Datos hardcodeados (reemplazar por endpoint del backend)
const historias = ref([
  {
    slug: "osvaldo-bianchi",
    nombre: "Osvaldo 'El Maestro' Bianchi",
    club: "Club Atlético Belgrano",
    años: 50,
    quote:
      "Las bochas me dieron mis mejores amigos y la disciplina que usé en toda mis competencias y en mi vida.",
    imagen:
      "",
    texto: [
      "La pasión por las bochas se vive intensamente en Córdoba, dónde cada lanzamiento cuenta una historia. Desde los inicios de la Federación de Bochas, hemos sido testigos de momentos inolvidables que han forjado la identidad de este deporte. La dedicación y el esfuerzo de nuestros jugadores han llevado a la creación de un legado que perdura en el tiempo.",
      "En cada torneo, se respira la emoción de la competencia, dónde los equipos luchan por la victoria y los aficionados apoyan con fervor. Las bochas no solo son un juego, son un vínculo que une a generaciones, creando amistades y recuerdos que trascienden el campo de juego. La historia de nuestros clubes, cómo el Club Atlético Belgrano, es un testimonio de la pasión que compartimos.",
      "A través de estas páginas, exploraremos las vivencias de quienes han hecho de las bochas su vida, desde los grandes campeones hasta los aficionados que llenan las gradas. Cada relato es un homenaje a la comunidad bochera de Córdoba, que sigue creciendo y vibrando con cada partido.",
    ],
  },
  {
    slug: "nelida-garcia",
    nombre: "Nélida 'Pochi' García",
    club: "Club Social y Deportivo Brinkmann",
    años: 32,
    quote:
      "Empecé cuándo casi no había mujeres en las canchas. Hoy ver a las chicas competir es mi mayor orgullo.",
    imagen: "",
    texto: [
      "La pasión por las bochas se vive intensamente en Córdoba, dónde cada lanzamiento cuenta una historia. Desde los inicios de la Federación de Bochas, hemos sido testigos de momentos inolvidables que han forjado la identidad de este deporte.",
      "Empecé a jugar bochas en una época dónde las mujeres no tenían mucho lugar en el deporte. Con esfuerzo y perseverancia, fui abriendo camino y hoy veo a muchas chicas competir, lo cuál me llena de orgullo.",
    ],
  },
  {
    slug: "facundo-capdevila",
    nombre: "Facundo Capdevila",
    club: "Asociación Juaréz Celman",
    años: 12,
    quote:
      "Herede las bochas de mi abuelo. Es un deporte de precisión, pero sobre todo de mucha cabeza.",
    imagen:
      "",
    texto: [
      "Desde chico mi abuelo me llevaba a la cancha. Aprendí mirando y después jugando. Las bochas son un deporte de precisión, pero sobre todo de mucha cabeza y estrategia.",
      "Cada lanzamiento es un desafío mental, hay que pensar bien dónde apuntar. La concentración es clave y eso me ha servido también en mi vida profesional.",
    ],
  },
  {
    slug: "marta-sierras",
    nombre: "Marta de las Sierras",
    club: "Club Alianza de Jesús María",
    años: 45,
    quote:
      "La cancha es el patio de mi casa. No me imagino un domingo sin el ruido de las bochas chocando.",
    imagen:
      "",
    texto: [
      "Crecí en una familia bochera. Mi padre y mis tíos jugaban, y yo aprendí desde muy chica. Las bochas son parte de mi vida, de mi familia y de mi comunidad.",
      "Los domingos en la cancha son sagrados. Es un momento de encuentro con amigos, con vecinos, de compartir la pasión por este deporte que nos une.",
    ],
  },
  {
    slug: "ricardo-rossi",
    nombre: "Ricardo 'Turco' Rossi",
    club: "Sociedad Italiana de Morteros",
    años: 55,
    quote:
      "Para arrimar bien hay que tener el pulso de un cirujano y la paciencia de un santo.",
    imagen:
      "",
    texto: [
      "Llevo más de 50 años jugando a las bochas. He visto crecer este deporte y he sido parte de su historia. La constancia y la paciencia son las claves para mantenerse vigente.",
      "Las bochas no son solo un juego, son una forma de vida. Cada domingo es una oportunidad para encontrarse con amigos, competir sanamente y disfrutar de la tradición.",
    ],
  },
  {
    slug: "sofia-luna",
    nombre: "Sofía Luna",
    club: "Club San Jorge de Arroyito",
    años: 8,
    quote:
      "Es el deporte más federal que tenemos. En cada pueblo hay una cancha y una historia esperando ser contada.",
    imagen:
      "",
    texto: [
      "Empecé a jugar hace 8 años y me enamoré de las bochas. Es un deporte dónde todos tienen lugar, no importa el pueblo o la ciudad de dónde vengas.",
      "Las bochas son el deporte más federal que tenemos. En cada pueblo hay una cancha y siempre hay una historia esperando ser contada. Eso es lo que más me gusta.",
    ],
  },
]);

const historia = ref(null);

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function load() {
  historia.value = historias.value.find((h) => h.slug === slug.value) || null;
  if (historia.value) {
    applyContentSeo({
      title: historia.value.nombre,
      description: historia.value.quote,
      content: historia.value.texto?.join(" "),
      image: historia.value.imagen,
    });
  } else {
    applyNotFoundSeo();
  }
}

onMounted(load);
watch(slug, load);

// ===== Compartir =====
function compartirWhatsapp() {
  const url = window.location.href;
  const text = historia.value?.nombre || "";
  window.open(
    `https://api.whatsapp.com/send?text=${encodeURIComponent(
      text + " " + url,
    )}`,
    "_blank",
    "noopener,noreferrer",
  );
}

function compartirFacebook() {
  const url = window.location.href;
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    "_blank",
    "noopener,noreferrer,width=600,height=400",
  );
}

function compartirInstagram() {
  const url = window.location.href;
  const text = `${historia.value?.nombre || ""} ${url}`;
  if (navigator.share) {
    navigator
      .share({ title: historia.value?.nombre || "", text, url })
      .catch(() => {});
  } else {
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  }
}
</script>
